/* -----------------UI--------------- */
import theme from "@configs/theme";
import type { Stat } from "components/RowStats";
import type { TaskCardProps } from "../types";

import { getParentMetadata } from "@helpers/content/metadata";

/* -----------------Types--------------- */
import type { Distribution } from "@memorang/types";
import type { DiagnosticCardProps } from "types/tag";

/* -----------------Child components--------------- */
import { useExamContext } from "@features/exam";
import { useProethicaSessionStore } from "@features/session/hooks/useProethicaSessionStore";

import { isScreenshotMode } from "@constants/common";
import { isMockedData, praxisLogo } from "@constants/demo";
import { mockedHomePageHeaderStats } from "@constants/mocked-data";
import { hasProductAccess } from "@helpers/content/tag";
import { randomizeDistribution } from "@helpers/mocked-helpers";
import { isWeb } from "@helpers/platform";
import useAppConfigIds from "@hooks/useAppConfigIds";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { useProductMetadata } from "@hooks/useProduct";
import { isDev, isStaging } from "@memorang/constants";
import { router } from "expo-router";
import type { TFunction } from "i18next";
import { useState } from "react";
/* -----------------i18n--------------- */
import { useTranslation } from "react-i18next";
/* -----------------API Calls--------------- */
import { getWeakTagsIds } from "../helpers/common";
import {
	getModuleLabel,
	getProethicaModuleListProps,
	getProethicaModuleTags,
} from "../helpers/proethica-module-list";
import { getExamDaysLeft, getExamStatus } from "../helpers/stats";
import type { HomeSection } from "../types";
import { useMasteryKeyTopicsSectionData } from "./useMasteryKeyTopicsSectionData";
import { useRecommendationSection } from "./useRecommendationSection";

type GetHeaderDataProps = {
	distribution: Distribution;
	examIcon?: string;
	examName?: string;
	examDate?: string;
	isDarkMode?: boolean;
	onDateStatItemClick?: () => void;
	t: TFunction;
	showPracticeWeak?: boolean;
	hasAccess?: boolean;
	accuracy?: number;
	demoExamId?: string;
};

const getHeaderData = ({
	distribution,
	examName,
	examDate,
	isDarkMode,
	onDateStatItemClick,
	t,
	accuracy,
	examIcon,
}: GetHeaderDataProps) => {
	if (!distribution) {
		return undefined;
	}
	const examStatusText = getExamStatus(examDate);

	const answered =
		Object.values(distribution).reduce((a, b) => a + b, 0) - distribution.none;
	const masteryPercentage = accuracy ? Math.round(accuracy) : 0;

	const daysLeft = getExamDaysLeft(examDate);

	const headerStats: Stat[] = [
		{
			icon: "brain",
			value: t("dashboard.stats.masteryValue", { value: masteryPercentage }),
			label: t("dashboard.stats.mastery"),
			color: theme(isDarkMode).palette.purple.light,
		},
		{
			icon: "check-circle-outline",
			value: t("dashboard.stats.completedValue", { count: answered }),
			label: t("dashboard.stats.completed"),
			color: theme(isDarkMode).palette.success.light,
		},
		{
			label: examDate
				? t("dashboard.stats.untilExam")
				: t("dashboard.stats.examDate"),
			icon: "calendar-outline",
			color: theme(isDarkMode).colors.primary,
			labelColor: theme(isDarkMode).colors.primary,
			onClick: onDateStatItemClick,
			value: examDate
				? daysLeft && daysLeft > 0
					? t("dashboard.stats.daysLeft", { count: daysLeft })
					: t("dashboard.stats.pastDue")
				: t("dashboard.stats.setExamDate"),
		},
	];

	return {
		distribution: isScreenshotMode
			? randomizeDistribution(distribution)
			: distribution,
		headerStats: isScreenshotMode
			? mockedHomePageHeaderStats(isDarkMode)
			: headerStats,
		masteryPercentage,
		examIcon,
		examName,
		examStatusText,
		examDate,
	};
};

const homeMenus = [
	{
		type: "store" as const,
		label: "Store",
		icon: "cart-outline",
	},
	{
		type: "settings" as const,
		label: "Settings",
		icon: "cog-outline",
	},
];

export const useHome = () => {
	const { t } = useTranslation();
	const {
		currentExamContext: { examDate },
	} = useExamContext();

	const theme = useAppTheme();

	const isDarkMode = theme.dark;

	const { data, isLoading } = useMixedItemDist();
	const [showDatePicker, setShowDatePicker] = useState(false);

	const [selectedWeakTags, setSelectedWeakTags] = useState<
		string[] | undefined
	>(undefined);

	const [showQuickStudyDialog, setShowQuickStudyDialog] = useState(false);

	const toggleShowDatePicker = () => {
		setShowDatePicker((prev) => !prev);
	};

	const mixedItemDist = data;

	const { data: productMetadata, isLoading: isProductMetadataLoading } =
		useProductMetadata();

	const { productUuid } = useAppConfigIds();

	const finalIsLoading = isLoading || isProductMetadataLoading;

	const hasAccess =
		(isDev && !isStaging) || isMockedData
			? true
			: hasProductAccess(productMetadata, productUuid);

	const demoCurrentExam = useDemoExamStore((state) => state.currentExam);
	const sessionByModule = useProethicaSessionStore(
		(state) => state.sessionByModule,
	);
	// const hasAccess = false;
	const contents = mixedItemDist?.contents || [];
	const numItems = contents.reduce((acc, curr) => acc + curr.totalCount, 0);

	const { distribution } = getParentMetadata(contents, numItems);
	const weakTagsIds = mixedItemDist ? getWeakTagsIds(mixedItemDist) : [];

	const headerData = mixedItemDist
		? getHeaderData({
				distribution,
				examIcon: theme.dark ? praxisLogo.light : praxisLogo.dark,
				examName: demoCurrentExam?.name,
				examDate,
				isDarkMode,
				onDateStatItemClick: toggleShowDatePicker,
				t,
				accuracy: mixedItemDist.accuracy,
				demoExamId: demoCurrentExam?.id,
			})
		: undefined;

	const exams = mixedItemDist?.exams;

	const handleSessionConfigNavigation = (tagIds: string[]) => {
		if (isWeb) {
			setSelectedWeakTags(tagIds);
		} else {
			router.push({
				pathname: "/session/customize",
				params: {
					selectedTagIds: tagIds,
				},
			});
		}
	};

	const masteryKeyTopicsSectionData = useMasteryKeyTopicsSectionData(
		mixedItemDist,
		hasAccess,
	);

	const recommendationSectionHookData = useRecommendationSection(
		mixedItemDist ? distribution : undefined,
	);

	const genericRecommendationItems = (recommendationSectionHookData
		?.tasksSection?.data ?? []) as TaskCardProps[];
	const moduleTags = getProethicaModuleTags(mixedItemDist?.tags);
	const isProethica = demoCurrentExam?.id === "proethica";
	type ProethicaRecommendation = TaskCardProps &
		DiagnosticCardProps & {
			moduleNumber: number;
		};
	const proethicaRecommendations: ProethicaRecommendation[] = moduleTags
		.map((tag) => {
			const module = getProethicaModuleListProps(
				tag,
				t,
				sessionByModule[String(tag.id)]?.status,
			);
			const moduleNumberMatch = getModuleLabel(tag).match(/\d+/);
			const moduleNumber =
				module.moduleNumber ??
				(moduleNumberMatch
					? Number(moduleNumberMatch[0])
					: Number.MAX_SAFE_INTEGER);
			const numCompleted =
				module.status === "COMPLETE"
					? module.numItems
					: Math.max(
							0,
							module.numItems - (tag.distribution.none ?? module.numItems),
						);
			const buttonIcon: DiagnosticCardProps["button"]["icon"] =
				module.status === "COMPLETE"
					? "chart-bar"
					: module.status === "ACTIVE"
						? "play-pause"
						: "play-outline";
			const handleClickTask = () => {
				if (module.status === "COMPLETE") {
					router.push("/(protected)/(tabs)/progress");
					return;
				}
				handleSessionConfigNavigation([module.id]);
			};
			return {
				title: module.title,
				subtitle: `${module.typeLabel} • ${module.durationLabel}`,
				status: module.status,
				handleClickTask,
				handleClickCard: handleClickTask,
				icon: "school-outline" as const,
				moduleNumber,
				numItems: module.numItems,
				numCompleted,
				thumbnailSource: module.thumbnailSource ?? module.thumbnailUrl,
				button: {
					cta: module.cta,
					icon: buttonIcon,
					handleClick: handleClickTask,
				},
				body:
					module.status === "ACTIVE"
						? "Continue this module from where you left off."
						: module.status === "COMPLETE"
							? "Review your results and key takeaways."
							: "Start this module to keep your momentum.",
			};
		})
		.sort((a, b) => {
			const statusPriority = {
				ACTIVE: 0,
				NOT_STARTED: 1,
				COMPLETE: 2,
				LEGACYREPORT: 3,
				MIGRATEDREPORT: 4,
			} as const;
			const statusDiff = statusPriority[a.status] - statusPriority[b.status];
			if (statusDiff !== 0) {
				return statusDiff;
			}
			return a.moduleNumber - b.moduleNumber;
		});
	const findProethicaModule = (moduleNumber: number) =>
		proethicaRecommendations.find(
			(module) => module.moduleNumber === moduleNumber,
		);
	const activeRecommendations = isProethica
		? proethicaRecommendations
		: genericRecommendationItems;
	const openQuickStudyDialog = () => setShowQuickStudyDialog(true);
	const withQuickStudyHandlers = (
		recommendation: ProethicaRecommendation,
	): ProethicaRecommendation =>
		recommendation.status === "COMPLETE"
			? recommendation
			: {
					...recommendation,
					handleClickTask: openQuickStudyDialog,
					handleClickCard: openQuickStudyDialog,
					button: {
						...recommendation.button,
						handleClick: openQuickStudyDialog,
					},
				};
	const baseQuickStudyRecommendation =
		findProethicaModule(3) ?? proethicaRecommendations[0];
	const quickStudyRecommendation = isProethica
		? baseQuickStudyRecommendation &&
			withQuickStudyHandlers(baseQuickStudyRecommendation)
		: activeRecommendations[0];
	const followUpRecommendations = activeRecommendations.slice(1, 3);
	const proethicaRecommendationCards = isProethica
		? [findProethicaModule(3), findProethicaModule(7)]
				.filter(
					(module): module is ProethicaRecommendation => module !== undefined,
				)
				.map(withQuickStudyHandlers)
		: undefined;

	const showPurchaseDialog = recommendationSectionHookData?.showPurchaseDialog;
	const toggleShowPurchaseDialog =
		recommendationSectionHookData?.toggleShowPurchaseDialog;

	const showCollectUserDetailsDialog =
		recommendationSectionHookData?.showCollectUserDetailsDialog;
	const toggleShowCollectUserDetailsDialog =
		recommendationSectionHookData?.toggleShowCollectUserDetailsDialog;

	const olxExamEligibility = recommendationSectionHookData?.olxExamEligibility;
	const setOlxExamEligibility =
		recommendationSectionHookData?.setOlxExamEligibility;
	const hideModulesOnHome = false;
	const masterySection: HomeSection | null =
		masteryKeyTopicsSectionData && !hideModulesOnHome
			? {
					title: masteryKeyTopicsSectionData.sectionTitle,
					type: masteryKeyTopicsSectionData.type,
					loading: masteryKeyTopicsSectionData.loading,
					chipProps:
						"chipProps" in masteryKeyTopicsSectionData
							? masteryKeyTopicsSectionData.chipProps
							: undefined,
					onPress: masteryKeyTopicsSectionData.onPress,
					data: [
						{
							type: masteryKeyTopicsSectionData.type,
							items: masteryKeyTopicsSectionData.data,
						},
					],
				}
			: null;
	const sections: HomeSection[] = [
		...(!isProethica && followUpRecommendations.length
			? [
					{
						title: t("dashboard.recommendations.title"),
						type: "tasks",
						loading: recommendationSectionHookData?.tasksSection?.loading,
						onPress: undefined,
						data: [
							{
								type: "tasks",
								items: followUpRecommendations,
							},
						],
					} satisfies HomeSection,
				]
			: []),
		...(masterySection ? [masterySection] : []),
	];

	return {
		headerData,
		weakTagsIds,
		sections,
		homeMenus,
		showPurchaseDialog,
		toggleShowPurchaseDialog,
		olxExamEligibility,
		setOlxExamEligibility,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		isLoading: finalIsLoading,
		quickStudyRecommendation,
		recommendationItems: proethicaRecommendationCards,
		showDatePicker,
		toggleShowDatePicker,
		hasAccess,
		handleSessionConfigNavigation,
		selectedWeakTags,
		setSelectedWeakTags,
		showQuickStudyDialog,
		setShowQuickStudyDialog,
		exams,
	};
};
